import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { GameAction, GameState, OfflineSummary } from "../shared/game-types";
import { ApiRequestError } from "../api/client";
import { bootstrapGame, submitGameAction } from "../api/game";
import { DEFAULT_SECONDS_PER_GAME_YEAR } from "../game-data/balance";
import { useAuth } from "./AuthContext";
import { useParams } from "react-router-dom";

interface PendingAction {
  action: GameAction;
  key: string;
}

interface GameContextValue {
  saveId: string;
  state: GameState | null;
  offline: OfflineSummary | null;
  stateVersion: number;
  secondsPerGameYear: number;
  clockSyncedAt: number;
  status: "idle" | "loading" | "ready" | "error";
  busy: boolean;
  error: string | null;
  notice: string[];
  pendingAction: PendingAction | null;
  refresh: () => Promise<void>;
  perform: (action: GameAction) => Promise<boolean>;
  retryPending: () => Promise<boolean>;
  dismissPending: () => void;
  dismissOffline: () => void;
  clearFeedback: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const { saveId = "" } = useParams<{ saveId: string }>();
  const [state, setState] = useState<GameState | null>(null);
  const [offline, setOffline] = useState<OfflineSummary | null>(null);
  const [stateVersion, setStateVersion] = useState(0);
  const [secondsPerGameYear, setSecondsPerGameYear] = useState(DEFAULT_SECONDS_PER_GAME_YEAR);
  const [clockSyncedAt, setClockSyncedAt] = useState(monotonicNow);
  const [status, setStatus] = useState<GameContextValue["status"]>("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string[]>([]);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const ownerRef = useRef<string | null>(null);
  const requestEpoch = useRef(0);

  const clearState = useCallback(() => {
    requestEpoch.current += 1;
    setState(null);
    setOffline(null);
    setStateVersion(0);
    setSecondsPerGameYear(DEFAULT_SECONDS_PER_GAME_YEAR);
    setClockSyncedAt(monotonicNow());
    setPendingAction(null);
    setNotice([]);
    setError(null);
    setStatus("idle");
    ownerRef.current = null;
  }, []);

  const refresh = useCallback(async () => {
    if (!auth.user) {
      clearState();
      return;
    }
    const epoch = ++requestEpoch.current;
    setStatus("loading");
    setError(null);
    try {
      const result = await bootstrapGame(saveId);
      if (epoch !== requestEpoch.current) return;
      const ownerKey = `${auth.user.id}:${saveId}`;
      if (ownerRef.current && ownerRef.current !== ownerKey) return;
      ownerRef.current = ownerKey;
      setState(result.state);
      setOffline(result.offline);
      setStateVersion(result.stateVersion);
      setSecondsPerGameYear(validTimeScale(result.secondsPerGameYear));
      setClockSyncedAt(monotonicNow());
      setStatus("ready");
    } catch (cause) {
      if (epoch !== requestEpoch.current) return;
      if (cause instanceof ApiRequestError && (cause.status === 401 || cause.code === "AUTH_REQUIRED")) {
        clearState();
        auth.invalidate();
        return;
      }
      setError(cause instanceof Error ? cause.message : "无法载入游戏存档。");
      setStatus("error");
    }
  }, [auth.user, auth.invalidate, clearState, saveId]);

  useEffect(() => {
    if (!auth.user) clearState();
    else if (saveId && ownerRef.current !== `${auth.user.id}:${saveId}`) void refresh();
  }, [auth.user, clearState, refresh, saveId]);

  const send = useCallback(async (pending: PendingAction): Promise<boolean> => {
    if (!auth.csrfToken) {
      clearState();
      auth.invalidate();
      return false;
    }
    setBusy(true);
    setError(null);
    setNotice([]);
    try {
      const result = await submitGameAction(saveId, pending.action, auth.csrfToken, pending.key);
      setState(result.state);
      setStateVersion((version) => result.stateVersion ?? version + 1);
      setSecondsPerGameYear(validTimeScale(result.secondsPerGameYear));
      setClockSyncedAt(monotonicNow());
      setNotice(result.messages);
      setPendingAction(null);
      setStatus("ready");
      return true;
    } catch (cause) {
      if (cause instanceof ApiRequestError && (cause.status === 401 || cause.code === "AUTH_REQUIRED")) {
        clearState();
        auth.invalidate();
      } else {
        const uncertain = cause instanceof ApiRequestError && (cause.code === "NETWORK_ERROR" || cause.status >= 500);
        if (uncertain) setPendingAction(pending);
        else setPendingAction(null);
        setError(cause instanceof Error ? cause.message : "操作失败。");
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [auth.csrfToken, auth.invalidate, clearState, saveId]);

  const perform = useCallback(async (action: GameAction) => {
    if (busy) return false;
    const pending = { action, key: crypto.randomUUID() };
    return send(pending);
  }, [busy, send]);

  const retryPending = useCallback(async () => {
    if (!pendingAction || busy) return false;
    return send(pendingAction);
  }, [busy, pendingAction, send]);

  const dismissPending = useCallback(() => {
    setPendingAction(null);
    setError(null);
  }, []);

  const dismissOffline = useCallback(() => setOffline(null), []);

  const clearFeedback = useCallback(() => {
    setError(null);
    setNotice([]);
  }, []);

  const value = useMemo<GameContextValue>(() => ({
    saveId,
    state,
    offline,
    stateVersion,
    secondsPerGameYear,
    clockSyncedAt,
    status,
    busy,
    error,
    notice,
    pendingAction,
    refresh,
    perform,
    retryPending,
    dismissPending,
    dismissOffline,
    clearFeedback,
  }), [
    saveId, state, offline, stateVersion, secondsPerGameYear, clockSyncedAt, status, busy, error, notice, pendingAction,
    refresh, perform, retryPending, dismissPending, dismissOffline, clearFeedback,
  ]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const value = useContext(GameContext);
  if (!value) throw new Error("useGame must be used inside GameProvider");
  return value;
}

export function useLiveGameTime(updateEveryMs = 1_000): number {
  const { state, secondsPerGameYear, clockSyncedAt } = useGame();
  const [now, setNow] = useState(monotonicNow);
  useEffect(() => {
    setNow(monotonicNow());
    const timer = window.setInterval(() => setNow(monotonicNow()), updateEveryMs);
    return () => window.clearInterval(timer);
  }, [clockSyncedAt, updateEveryMs]);
  if (!state) return 0;
  return state.meta.gameTimeYears + Math.max(0, now - clockSyncedAt) / 1_000 / secondsPerGameYear;
}

function monotonicNow(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function validTimeScale(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_SECONDS_PER_GAME_YEAR;
}
